import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { brand } from "../../../config/appData";
import { useAppDispatch, useResetPasswordData } from "../../../Utility";
import { onResetPassword, onResetPasswordReset } from "../../../Store/Slices/resetPasswordSlice";
import "./ResetPasswordPage.css";

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() || "";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const resetData = useResetPasswordData();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const isLoading = resetData?.isLoading ?? false;
  const isSuccess = (resetData?.status_code === 200 || resetData?.status_code === "200");
  const isError = resetData?.isError && resetData?.status_code != null && !isSuccess;

  useEffect(() => {
    dispatch(onResetPasswordReset());
  }, [dispatch]);

  useEffect(() => {
    if (isSuccess) {
      setSubmitted(true);
    }
  }, [isSuccess]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (!token) {
      setErrors({ form: "Invalid or missing reset link. Please request a new one." });
      return;
    }

    const newPwd = newPassword.trim();
    const confirmPwd = confirmPassword.trim();

    const newErrors = {};
    if (!newPwd) {
      newErrors.newPassword = "New password is required.";
    } else if (newPwd.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters.";
    }
    if (!confirmPwd) {
      newErrors.confirmPassword = "Please confirm your new password.";
    } else if (newPwd !== confirmPwd) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    dispatch(
      onResetPassword({
        token,
        newPassword: newPwd,
      })
    );
  };

  if (submitted) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <div className="reset-password-brand" onClick={() => navigate("/")}>
              <span className="reset-password-logo">{brand.logoIcon}</span>
              <span className="reset-password-name">{brand.name}</span>
            </div>
            <h1 className="reset-password-title">Password reset</h1>
            <p className="reset-password-subtitle">
              Your password has been reset successfully. You can now log in with your new password.
            </p>
          </div>
          <Link to="/login" className="reset-password-btn">
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-card">
        <Link to="/login" className="reset-password-back" aria-label="Back to login">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to login
        </Link>
        <div className="reset-password-header">
          <div className="reset-password-brand" onClick={() => navigate("/")}>
            <span className="reset-password-logo">{brand.logoIcon}</span>
            <span className="reset-password-name">{brand.name}</span>
          </div>
          <h1 className="reset-password-title">Reset your password</h1>
          <p className="reset-password-subtitle">
            Enter your new password below.
          </p>
        </div>

        {!token && (
          <div className="reset-password-error-box">
            Invalid or missing reset link. Please use the link from your email or{" "}
            <Link to="/forgot-password">request a new one</Link>.
          </div>
        )}

        {(errors.form || isError) && (
          <div className="reset-password-error-box">
            {errors.form || resetData?.message}
          </div>
        )}

        {token && (
          <form className="reset-password-form" onSubmit={handleSubmit}>
            <div className="reset-password-group">
              <label htmlFor="reset-new-password" className="reset-password-label">
                New password
              </label>
              <div className="reset-password-input-wrap">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="reset-new-password"
                  className={`reset-password-input ${errors.newPassword ? "input-error" : ""}`}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (errors.newPassword) setErrors((p) => ({ ...p, newPassword: "" }));
                  }}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="reset-password-toggle"
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowNewPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showNewPassword ? "Hide" : "Show"}
                </button>
              </div>
              {errors.newPassword && (
                <span className="reset-password-field-error">{errors.newPassword}</span>
              )}
            </div>

            <div className="reset-password-group">
              <label htmlFor="reset-confirm-password" className="reset-password-label">
                Confirm new password
              </label>
              <div className="reset-password-input-wrap">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="reset-confirm-password"
                  className={`reset-password-input ${errors.confirmPassword ? "input-error" : ""}`}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors((p) => ({ ...p, confirmPassword: "" }));
                  }}
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="reset-password-toggle"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="reset-password-field-error">{errors.confirmPassword}</span>
              )}
            </div>

            <button
              type="submit"
              className={`reset-password-btn ${isLoading ? "btn-loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? "Resetting..." : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
